<script setup lang="ts">
import { computed } from 'vue';

import { DateFormat, SemboDate } from '@libs/sembo-dates';
import { AppSvgIcon, AppFadeTransition } from '@libs/ui';
import type { CssCustomPropertyFont } from '@libs/ui';

import MonthsSelectorInnerView from './MonthsSelectorInnerView.vue';
import type { MonthsSelectorMonth } from './models';

interface MonthsSelectorProps {
  shouldShowMonthsSelector: boolean;
  months: MonthsSelectorMonth[];
  selectedMonthIsoDate: string;
  buttonFont: CssCustomPropertyFont;
}

const props = defineProps<MonthsSelectorProps>();

interface MonthsSelectorEmits {
  (eventName: 'select-month', month: Date): void;
  (eventName: 'toggle-months-selector'): void;
}

const emit = defineEmits<MonthsSelectorEmits>();

const chevronIcon = computed<'chevronDown' | 'chevronUp'>(() =>
  props.shouldShowMonthsSelector ? 'chevronUp' : 'chevronDown'
);

const currentMonth = computed<string>(() => {
  const date = new SemboDate(new Date(props.selectedMonthIsoDate));
  return date.format(DateFormat.FullMonthAndYear);
});
</script>

<template>
  <div class="months-selector">
    <div class="actions">
      <button
        class="month-button"
        :class="{ [props.buttonFont]: true }"
        @click="emit('toggle-months-selector')"
      >
        {{ currentMonth }}
        <AppSvgIcon :name="chevronIcon" size="16" color="color-soft-black" />
      </button>
    </div>

    <div class="months">
      <AppFadeTransition transition-time="short">
        <MonthsSelectorInnerView
          v-if="shouldShowMonthsSelector"
          :months="props.months"
          :selected-month-iso-date="props.selectedMonthIsoDate"
          @confirm="emit('select-month', $event)"
        />
      </AppFadeTransition>
    </div>
  </div>
</template>

<style scoped lang="scss">
.months-selector {
  width: 100%;
  height: 100%;
  position: relative;

  .actions {
    width: 100%;
    display: flex;
    justify-content: space-between;

    .month-button {
      all: unset;
      cursor: pointer;
      color: var(--color-soft-black);

      display: flex;
      align-items: center;
      gap: var(--spacing-8);

      &.font-label-xl-light {
        @include font-label-xl-light;
      }

      &.font-label-l-bold {
        @include font-label-l-bold;
      }
    }
  }

  .months {
    background: var(--color-white);
    position: absolute;
    top: 80%;
    left: 0;
    width: 100%;
  }
}
</style>
